import { z } from 'zod';

// ── DOB Schema ──────────────────────────────────────────────────────────────
export const dobSchema = z.object({
    dob: z
        .string()
        .min(1, 'Date of birth is required')
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Please enter a valid date of birth')
        .refine((val) => {
            const date = new Date(val);
            return !isNaN(date.getTime());
        }, 'Please enter a valid date of birth')
        .refine((val) => {
            return new Date(val) < new Date();
        }, 'Date of birth cannot be in the future')
});

// ── Allowed ENUM values matching patient_pref-service validators ──────────────
export const COMM_CHANNEL_VALUES  = ['SMS', 'EMAIL', 'PHONE'];
export const CONTACT_TIME_OF_DAY  = ['Morning', 'Late Morning', 'Early Afternoon', 'Mid Afternoon', 'Evening'];
export const APPT_TIME_OF_DAY     = ['Morning', 'Late Morning', 'Early Afternoon', 'Mid Afternoon', 'Evening'];
export const DAYS_OF_WEEK        = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
export const TECH_COMFORT_LEVELS = ['Very comfortable', 'Somewhat comfortable', 'Not very comfortable', 'Never tried'];
export const TECH_COMFORT_OPTIONS = [
    { label: 'Very comfortable',       value: 'Very comfortable' },
    { label: 'Somewhat comfortable',   value: 'Somewhat comfortable' },
    { label: 'Not very comfortable',   value: 'Not very comfortable' },
    { label: 'Never tried',            value: 'Never tried' }
];

export const COMM_CHANNEL_OPTIONS = [
    { label: 'Text Message (SMS)', value: 'SMS' },
    { label: 'Email',              value: 'EMAIL' },
    { label: 'Phone Call',         value: 'PHONE' }
];

// Display labels with time ranges shown in the UI (maps to API enum values)
export const CONTACT_TIME_LABELS = {
    'Morning'           : 'Morning (8:00 AM – 10:00 AM)',
    'Late Morning'      : 'Late Morning (10:00 AM – 12:00 PM)',
    'Early Afternoon'   : 'Early Afternoon (12:00 PM – 2:00 PM)',
    'Mid Afternoon'     : 'Mid Afternoon (2:00 PM – 5:00 PM)',
    'Evening'           : 'Evening (5:00 PM – 7:00 PM)'
};

export const APPT_TIME_LABELS = {
    'Morning'           : 'Morning (8:00 AM – 10:00 AM)',
    'Late Morning'      : 'Late Morning (10:00 AM – 12:00 PM)',
    'Early Afternoon'   : 'Early Afternoon (12:00 PM – 2:00 PM)',
    'Mid Afternoon'     : 'Mid Afternoon (2:00 PM – 5:00 PM)',
    'Evening'           : 'Evening (5:00 PM – 7:00 PM)'
};

// ── Preferences Submit Schema ────────────────────────────────────────────────
export const preferencesSchema = z.object({
    phone: z
        .string()
        .optional()
        .refine((val) => !val || /^[\d\s\-\+\(\)]{7,20}$/.test(val), {
            message: 'Please enter a valid phone number'
        }),

    email: z
        .string()
        .optional()
        .refine((val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
            message: 'Please enter a valid email address'
        }),

    commChannel: z
        .array(z.enum(COMM_CHANNEL_VALUES))
        .min(1, 'Please select at least one communication method')
        .max(3)
        .optional(),

    callPrefDayOfWeek: z
        .array(z.enum(DAYS_OF_WEEK))
        .min(1, 'Please select at least one preferred day')
        .max(5)
        .optional(),

    callPrefTimeOfDay: z
        .array(z.enum(CONTACT_TIME_OF_DAY))
        .min(1, 'Please select at least one preferred time')
        .max(5)
        .optional(),

    apptPrefTimeOfDay: z
        .array(z.enum(APPT_TIME_OF_DAY))
        .min(1, 'Please select at least one preferred appointment time')
        .max(5)
        .optional(),

    techComfortLevel: z
        .enum(TECH_COMFORT_LEVELS)
        .optional()
});